import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";
import { Plus, Trash, MagnifyingGlass, X } from "@phosphor-icons/react";
import { channelButtonClass } from "@/lib/ui-helpers";

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "whatsapp", external_id: "", handle: "", tags: "", notes: "" });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (channel) params.set("channel", channel);
    const { data } = await api.get(`/contacts?${params.toString()}`);
    setContacts(data);
  }, [q, channel]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contacts", {
        name: form.name,
        channel: form.channel,
        external_id: form.external_id,
        handle: form.handle || null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        notes: form.notes || null,
      });
      toast.success("Contact added");
      setShowForm(false);
      setForm({ name: "", channel: "whatsapp", external_id: "", handle: "", tags: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// leads</div>
          <h1 className="font-heading text-5xl tracking-tight mt-2">Contacts</h1>
        </div>
        <button
          data-testid="contact-new-button"
          onClick={() => setShowForm(true)}
          className="bg-white text-black px-4 py-2.5 rounded-sm flex items-center gap-2 hover:bg-white/90 transition-colors"
        >
          <Plus size={16} weight="bold" /> New contact
        </button>
      </div>

      <div className="p-10 space-y-6">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="contact-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search name or handle…"
              className="w-full bg-transparent border border-white/10 focus:border-white/40 rounded-sm pl-9 pr-3 py-2.5 outline-none transition-colors"
            />
          </div>
          <div className="flex gap-1 border border-white/10 rounded-sm p-1">
            {[["", "All"], ["whatsapp", "WhatsApp"], ["instagram", "Instagram"]].map(([v, l]) => (
              <button
                key={v}
                data-testid={`filter-channel-${v || "all"}`}
                onClick={() => setChannel(v)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-sm transition-colors ${
                  channel === v ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-white/10 text-[11px] uppercase tracking-widest font-mono text-white/40">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Channel</th>
                <th className="px-4 py-3">Handle / ID</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-white/40 font-mono text-sm">
                    no contacts yet — click New contact to add one
                  </td>
                </tr>
              )}
              {contacts.map((c) => (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3"><ChannelBadge channel={c.channel} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-white/60">{c.handle || c.external_id}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono uppercase tracking-wider text-white/60">{c.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {c.tags?.map((t) => (
                        <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border border-white/10 rounded-sm">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`contact-delete-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="p-1.5 hover:bg-white/10 rounded-sm text-white/40 hover:text-[#E1306C] transition-colors"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// new</div>
                <div className="font-heading text-2xl tracking-tight">New contact</div>
              </div>
              <button data-testid="contact-form-close" onClick={() => setShowForm(false)} className="p-2 hover:bg-white/10 rounded-sm transition-colors">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Name</label>
                <input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Channel</label>
                <div className="mt-1 flex gap-2">
                  {["whatsapp", "instagram"].map((c) => (
                    <button type="button" key={c} data-testid={`contact-form-channel-${c}`}
                      onClick={() => setForm({...form, channel: c})}
                      className={`flex-1 px-3 py-2.5 border rounded-sm text-sm uppercase tracking-wider font-mono transition-colors ${channelButtonClass(form.channel, c)}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">
                  {form.channel === "whatsapp" ? "Phone (E.164)" : "Instagram User ID"}
                </label>
                <input data-testid="contact-form-external-id" value={form.external_id} onChange={(e) => setForm({...form, external_id: e.target.value})} required
                  placeholder={form.channel === "whatsapp" ? "+5511999999999" : "17841400001234567"}
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors font-mono" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Handle / display</label>
                <input value={form.handle} onChange={(e) => setForm({...form, handle: e.target.value})}
                  placeholder="@username"
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Tags (comma separated)</label>
                <input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}
                  placeholder="warm, decision-maker"
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <button data-testid="contact-form-submit" type="submit"
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Create contact
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
